import React from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography, Pagination } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import type { IAppState } from '../redux/store/types';
import { deleteRedisKeys, listRedisKeys, type IRedisKeysResponse, type RedisKeysScope } from '../services/rest';

const ControlPlane: React.FC = () => {
  const JWT = useSelector((state: IAppState) => state.auth.JWT);

  const bodyScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [bodyScrollbarWidth, setBodyScrollbarWidth] = React.useState(0);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastAction, setLastAction] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<IRedisKeysResponse | null>(null);
  const [deletedCount, setDeletedCount] = React.useState<number | null>(null);
  const [keyFilter, setKeyFilter] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize] = React.useState(100);

  const filteredItems = React.useMemo(() => {
    if (!response?.items) return null;
    const filter = keyFilter.trim().toLowerCase();
    if (!filter) return response.items;
    return response.items.filter((it) => it.key.toLowerCase().includes(filter));
  }, [response?.items, keyFilter]);

  React.useLayoutEffect(() => {
    const measure = () => {
      const el = bodyScrollRef.current;
      if (!el) return;
      const scrollbarWidth = el.offsetWidth - el.clientWidth;
      setBodyScrollbarWidth(scrollbarWidth > 0 ? scrollbarWidth : 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [filteredItems?.length]);

  const lastHeaderCellPaddingRightPx = bodyScrollbarWidth > 0 ? bodyScrollbarWidth + 8 : 8;

  const runList = async (scope: RedisKeysScope, page: number = 1) => {
    if (!JWT) {
      setError('Missing JWT');
      return;
    }

    setLoading(true);
    setError(null);
    setDeletedCount(null);
    setLastAction(scope === 'websocket' ? 'List Websockets Redis keys' : 'List all Redis keys');
    setKeyFilter('');

    try {
      const result = await listRedisKeys(JWT, scope, page, pageSize);
      setResponse(result);
      setCurrentPage(page);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to list keys');
    } finally {
      setLoading(false);
    }
  };

  const runDelete = async (scope: RedisKeysScope) => {
    if (!JWT) {
      setError('Missing JWT');
      return;
    }

    const ok = window.confirm('Are you sure you want to delete Redis keys?');
    if (!ok) return;

    setLoading(true);
    setError(null);
    setResponse(null);
    setLastAction('Delete all Redis keys');
    setKeyFilter('');
    setCurrentPage(1);

    try {
      const result = await deleteRedisKeys(JWT, scope);
      setDeletedCount(result.deletedCount);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete keys');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    if (lastAction?.includes('Websockets')) {
      runList('websocket', page);
    } else {
      runList('all', page);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant='h4' gutterBottom>
        Control Plane
      </Typography>

      <Stack
        useFlexGap
        sx={{
          mb: 2,
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: { xs: 'stretch', sm: 'center' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: 2,
        }}
      >
        <Button variant='contained' onClick={() => runList('all')} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          List all Redis keys
        </Button>
        <Button variant='contained' onClick={() => runList('websocket')} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          List Websockets Redis keys
        </Button>
        <Button variant='contained' color='error' onClick={() => runDelete('all')} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          Delete all Redis keys
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lastAction && (
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          {lastAction}
        </Typography>
      )}

      {deletedCount !== null && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Deleted {deletedCount} keys
        </Alert>
      )}

      {response && (
        <>
          <Stack
            useFlexGap
            sx={{
              mb: 2,
              width: '100%',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: { xs: 'stretch', sm: 'space-between' },
              gap: 2,
            }}
          >
            <TextField
              size='small'
              label='Filter keys'
              value={keyFilter}
              onChange={(e) => setKeyFilter(e.target.value)}
              placeholder='Type part of a key...'
              sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 520 } }}
            />

            <Stack useFlexGap sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <Button variant='outlined' onClick={() => setKeyFilter('')} disabled={loading || !keyFilter.trim()}>
                Clear
              </Button>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                Showing {filteredItems?.length ?? 0} / {response.items.length} (Page {response.page} of {response.totalPages}, {response.totalCount} total)
              </Typography>
            </Stack>
          </Stack>

          {response.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Pagination
                count={response.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                disabled={loading}
                showFirstButton
                showLastButton
                size="medium"
              />
            </Box>
          )}
        </>
      )}

      {filteredItems && (
        <TableContainer
          component={Paper}
          ref={bodyScrollRef}
          sx={{
            width: '100%',
            maxHeight: 520,
            overflowX: 'auto',
            border: '1px solid rgba(0,0,0,0.12)',
            scrollbarGutter: 'stable',
          }}
        >
          <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 32,
                    minWidth: 32,
                    maxWidth: 32,
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    px: 0.5,
                    borderRight: '1px solid rgba(0,0,0,0.08)',
                  }}
                >
                  #
                </TableCell>
                <TableCell sx={{ width: '42%', pr: 2, borderRight: '1px solid rgba(0,0,0,0.08)' }}>Key</TableCell>
                <TableCell
                  sx={{
                    width: { xs: 48, sm: 90 },
                    minWidth: { xs: 48, sm: 90 },
                    maxWidth: { xs: 48, sm: 90 },
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    px: 0.5,
                    borderRight: '1px solid rgba(0,0,0,0.08)',
                  }}
                >
                  Type
                </TableCell>
                <TableCell sx={{ width: '57%', pl: 2, pr: `${lastHeaderCellPaddingRightPx}px` }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((it, idx) => {
                const valueStr = typeof it.value === 'string' ? it.value : JSON.stringify(it.value, null, 2);
                return (
                  <TableRow key={`${it.key}-${idx}`} hover>
                    <TableCell
                      sx={{
                        width: 32,
                        minWidth: 32,
                        maxWidth: 32,
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        px: 0.5,
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'monospace',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        pr: 2,
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      {it.key}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: { xs: 48, sm: 90 },
                        minWidth: { xs: 48, sm: 90 },
                        maxWidth: { xs: 48, sm: 90 },
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        px: 0.5,
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      {it.type}
                    </TableCell>
                    <TableCell sx={{ pl: 2 }}>
                      <Box
                        component='pre'
                        sx={{
                          m: 0,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          maxHeight: '30vh',
                          overflow: 'auto',
                        }}
                      >
                        {valueStr}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {response && response.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={response.totalPages}
            page={currentPage}
            onChange={handlePageChange}
            disabled={loading}
            showFirstButton
            showLastButton
            size="medium"
          />
        </Box>
      )}
    </Box>
  );
};

export default ControlPlane;
